const dispatchTimeZone = 'Europe/Amsterdam';
const dispatchDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: dispatchTimeZone,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function amsterdamParts(date) {
  return Object.fromEntries(
    dispatchDateFormatter.formatToParts(date)
      .filter(({ type }) => type !== 'literal')
      .map(({ type, value }) => [type, Number(value)])
  );
}

function amsterdamCutoff(day) {
  const wallTime = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), 16);
  let instant = wallTime;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const local = amsterdamParts(new Date(instant));
    const represented = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
    instant += wallTime - represented;
  }
  return instant;
}

export function getDispatchCountdown(now = new Date()) {
  const local = amsterdamParts(now);
  const today = Date.UTC(local.year, local.month - 1, local.day);
  const day = new Date(today);
  if (local.hour >= 16) day.setUTCDate(day.getUTCDate() + 1);
  while (day.getUTCDay() === 0 || day.getUTCDay() === 6) {
    day.setUTCDate(day.getUTCDate() + 1);
  }
  const deadline = amsterdamCutoff(day);
  return {
    deadline,
    isToday: day.getTime() === today,
    seconds: Math.max(0, Math.ceil((deadline - now.getTime()) / 1000)),
  };
}

export function formatDispatchDuration(seconds) {
  const total = Math.max(0, Math.ceil(seconds));
  return [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
}

if (typeof customElements !== 'undefined' && !customElements.get('ck-dispatch-countdown')) {
  class ClassickDispatchCountdown extends HTMLElement {
    connectedCallback() {
      this.stop();
      this.live = this.querySelector('[data-dispatch-live]');
      this.fallback = this.querySelector('[data-dispatch-fallback]');
      this.time = this.querySelector('[data-dispatch-time]');
      this.until = this.querySelector('[data-dispatch-until]');
      this.note = this.querySelector('[data-dispatch-note]');
      if (!this.live || !this.fallback || !this.time || !this.until || !this.note) return;
      try {
        this.dayFormatter = new Intl.DateTimeFormat(this.dataset.locale || 'nl', {
          timeZone: dispatchTimeZone,
          weekday: 'long',
        });
      } catch {
        this.dayFormatter = new Intl.DateTimeFormat('en', { timeZone: dispatchTimeZone, weekday: 'long' });
      }
      this.onVisibilityChange = () => this.start();
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    stop() {
      window.clearTimeout(this.timeout);
    }

    start() {
      this.stop();
      this.tick();
    }

    tick() {
      if (!this.isConnected) return;
      try {
        const current = getDispatchCountdown();
        const day = this.dayFormatter.format(new Date(current.deadline));
        const until = current.isToday ? this.dataset.untilToday : this.dataset.untilNext.replace('__DAY__', day);
        const note = current.isToday ? this.dataset.dispatchToday : this.dataset.dispatchNext.replace('__DAY__', day);
        this.time.textContent = formatDispatchDuration(current.seconds);
        if (this.until.textContent !== until) this.until.textContent = until;
        if (this.note.textContent !== note) this.note.textContent = note;
        this.live.hidden = false;
        this.fallback.hidden = true;
      } catch {
        this.live.hidden = true;
        this.fallback.hidden = false;
      }
      if (!document.hidden) this.timeout = window.setTimeout(() => this.tick(), 1000 - (Date.now() % 1000) + 20);
    }
  }

  customElements.define('ck-dispatch-countdown', ClassickDispatchCountdown);
}
